import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, RefreshCw, Filter, X, FilterX, Tag, ChevronDown, 
  Calendar, Database, User, PackagePlus, Package, Building2, ListPlus, List,
  DollarSign, Download, ChevronRight, Boxes, Banknote
} from 'lucide-react';
import FacturacionMenuHeader from '../components/FacturacionMenuHeader';
import FactLoader from './FactLoader';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import './DashboardBultos.css';
import * as XLSX from 'xlsx';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const PAGE_SIZE = 1000;

const filterLabels = {
  FACT_PROVEEDOR: "Proveedor",
  FACT_MARCA: "Marca",
  FACT_LINEA: "Línea",
  FACT_PORTAFOLIO: "Portafolio",
  FACT_CLIENTE_PRINCIPAL: "Cliente Principal",
  FACT_VENDEDOR_REGISTRO: "Vendedor Registro",
  FACT_VENDEDOR_ACTUAL: "Vendedor Actual",
  FACT_CANAL: "Canal",
  FACT_REGION: "Región",
  FACT_STATUS_ANULADA: "Anulada",
  FACT_PRODUCTO: "Producto",
  FACT_CLIENTE_NOMBRE: "Cliente Nombre",
  FACT_ART_ID: "ID Artículo",
  FACT_ADMIN: "Admin Registro",
  FACT_UND: "Unidad",
  FACT_IMP: "Impuesto",
  NCD_DEVOLUCION: "Devolución",
  NCD_OBSERVACION: "Obs. Devolución",
  NCF_NRO_DOC: "Nota Financiera",
  NCF_CONCEPTO_NOTA: "Concepto Nota",
  FACT_MONEDA: "Moneda",
  FACT_SEG_DES: "Segmento",
  FACT_FACTURA: "Factura"
};

const INITIAL_FILTERS = {
  FACT_PROVEEDOR: [], FACT_MARCA: [], FACT_LINEA: [], FACT_PORTAFOLIO: [],
  FACT_CLIENTE_PRINCIPAL: [], FACT_VENDEDOR_REGISTRO: [], FACT_VENDEDOR_ACTUAL: [],
  FACT_CANAL: [], FACT_REGION: [], FACT_STATUS_ANULADA: [], FACT_PRODUCTO: [], 
  FACT_CLIENTE_NOMBRE: [], FACT_ADMIN: [], FACT_UND: [], FACT_IMP: [],
  NCD_DEVOLUCION: [], NCD_OBSERVACION: [], NCF_NRO_DOC: [], NCF_CONCEPTO_NOTA: [], FACT_MONEDA: [], FACT_SEG_DES: [], FACT_FACTURA: []
};

const formatUSD = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);
const formatBs = (num) => new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'VES' }).format(num || 0);
const formatNum = (num) => new Intl.NumberFormat('de-DE').format(num || 0);

const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt => String(opt).toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 100);
  
  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button className={`filter-pill-button ${selectedValues.length > 0 ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{selectedValues.length > 0 ? (selectedValues.length === 1 ? selectedValues[0] : `${selectedValues.length} Selec.`) : 'Todos'}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className="filter-dropdown-panel">
          <div className="search-wrapper">
            <Search size={14} className="search-icon" />
            <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
          </div>
          <div className="options-list">
            {filteredOptions.map((opt, i) => (
              <label key={i} className="checkbox-row">
                <input type="checkbox" checked={selectedValues.includes(opt)} onChange={() => {
                  const newVals = selectedValues.includes(opt) ? selectedValues.filter(v => v !== opt) : [...selectedValues, opt];
                  onChange(name, newVals);
                }} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
          {selectedValues.length > 0 && <button className="reset-pill-link" onClick={() => onChange(name, [])}>Limpiar</button>}
        </div>
      )}
    </div>
  );
};

const NCredito = () => {
  const { factQuery, getFactFilters, loading, error } = useQuery();
  const { session } = useAuth();
  const [isQuerying, setIsQuerying] = useState(false);
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const filtersInitialized = useRef(false);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [dynamicYears, setDynamicYears] = useState([]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [globalTotals, setGlobalTotals] = useState({ bultos: 0, usd: 0 });

  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [expandedRows, setExpandedRows] = useState({});

  const searchRef = useRef(null);
  const tableContainerRef = useRef(null);


  const today = useMemo(() => new Date(), []);
  const nombreMesActual = useMemo(() => today.toLocaleString('es-ES', { month: 'long' }).toUpperCase(), [today]);

  const [selectedYear, setSelectedYear] = useState([today.getFullYear()]);
  const [selectedMonth, setSelectedMonth] = useState([nombreMesActual]);
  // Cambiamos a [] por defecto para ver todo el mes al entrar
  const [selectedDay, setSelectedDay] = useState([]); 

  const meses = useMemo(() => ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"], []);
  const days = useMemo(() => Array.from({length: 31}, (_, i) => i + 1), []);


  const pageTotalUSD = useMemo(() => {
    return rows.reduce((acc, row) => acc + (row.FACT_MONTO_USD || 0), 0);
  }, [rows]);


  const groupedRows = useMemo(() => {
  const grouped = {};

  rows.forEach((row) => {
    const factura = row.FACT_FACTURA || 'SIN_FACTURA';

    if (!grouped[factura]) {
      grouped[factura] = {
        factura,
        clienteId: row.FACT_CLIENTE_ID,
        cliente: row.FACT_CLIENTE_NOMBRE,
        moneda: row.FACT_MONEDA,
        fecha: row.display_date || row.FACT_FECHA,

        totalCantidad: 0,
        totalUSD: 0,
        totalBs: 0,
        totalCUND: 0,
        descuentoPct: 0,
        descuentoMontoBs: 0,
        descuentoMontoUSD: 0,
        netoFinalBs: 0,
        netoFinalUSD: 0,

        items: []
      };
    }

    grouped[factura].items.push(row);

    grouped[factura].totalCantidad += Number(row.FACT_CANTIDAD || 0);
    grouped[factura].totalCUND += Number(row.FACT_UNIDAD || 0);
    const descuentoPct = Number(row.FACT_DESC || 0);

    const netoBs = Number(row.FACT_NETO || 0);
    const netoUSD = Number(row.FACT_MONTO_USD || 0);

    const descuentoBs = netoBs * (descuentoPct / 100);
    const descuentoUSD = netoUSD * (descuentoPct / 100);

    const brutoBs = netoBs + descuentoBs;
    const brutoUSD = netoUSD + descuentoUSD;

    grouped[factura].descuentoMontoBs += descuentoBs;
    grouped[factura].descuentoMontoUSD += descuentoUSD;

    grouped[factura].netoFinalBs += netoBs;
    grouped[factura].netoFinalUSD += netoUSD;

    grouped[factura].totalBs += brutoBs;
    grouped[factura].totalUSD += brutoUSD;

  });

  Object.values(grouped).forEach(group => {
    group.descuentoPct =
      group.totalBs > 0
        ? (group.descuentoMontoBs / group.totalBs) * 100
        : 0;
  });

  return Object.values(grouped);
}, [rows]);

const toggleFactura = (factura) => {
  setExpandedRows(prev => ({
    ...prev,
    [factura]: !prev[factura]
  }));
};

  // Corregido: Función highlightMatch para resaltar texto
  const highlightMatch = (text, query) => {
    if (!query || typeof text !== 'string') return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, i) => 
          part.toLowerCase() === query.toLowerCase() ? (
            <strong key={i} style={{ color: '#00c6ff' }}>{part}</strong>
          ) : (
            part
          )
        )}
      </>
    );
  };

  useEffect(() => {
    const handleClick = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (searchValue.length < 2) { setSuggestions([]); return; }
    const term = searchValue.toLowerCase();
    const results = [];
    const categories = [
      { key: 'FACT_PRODUCTO', label: 'Producto', icon: <Package size={14}/> },
      { key: 'FACT_CLIENTE_NOMBRE', label: 'Cliente', icon: <User size={14}/> },
      { key: 'FACT_MARCA', label: 'Marca', icon: <Tag size={14}/> },
      { key: 'FACT_CLIENTE_PRINCIPAL', label: 'Grupo', icon: <Building2 size={14}/> }
    ];
    categories.forEach(cat => {
      const opts = availableOptions[cat.key] || config?.availableOptions?.[cat.key] || [];
      const matches = opts.filter(opt => String(opt).toLowerCase().includes(term)).slice(0, 5)
        .map(val => ({ key: cat.key, value: val, category: cat.key, catLabel: cat.label, icon: cat.icon }));
      results.push(...matches);
    });
    setSuggestions(results);
    setShowSuggestions(true);
  }, [searchValue, availableOptions, config]);

  const buildQueryParams = useCallback((pageNum = 1) => ({
    page_anios: selectedYear.map(Number),
    page_meses: selectedMonth,
    page_dias: selectedDay.map(Number),
    filters,
    page: pageNum,
    page_size: PAGE_SIZE,
    user_info: session?.user
  }), [selectedYear, selectedMonth, selectedDay, filters, session]);

    const executeQuery = useCallback(async (params) => {
    setIsQuerying(true);
    try {
      const result = await factQuery(params);
      
      // ✅ Extracción robusta de datos: Soporta arrays directos o múltiples nombres de tabla (Parquet)
      const tableData =
      result?.data?.rows ||
      result?.data ||
      result?.table_fact?.rows ||
      result?.table_fact ||
      [];

    const totals =
      result?.totals?.rows?.[0] ||
      result?.totals?.[0] ||
      {};

    console.log("PARSED DATA", tableData);
    console.log("PARSED TOTALS", totals);

    if (Array.isArray(tableData)) {
      setRows(tableData);

      setTotalCount(
        Number(
          totals.total_count ||
          totals.total_records ||
          tableData.length
        )
      );

      setGlobalTotals({
        bultos: Number(totals.bultos || 0),
        usd: Number(totals.usd || 0),
        total_facturas: Number(totals.total_facturas || 0),
        unidad: Number(totals.unidad || 0),
        bs: Number(totals.bs || 0)
      });


      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
    }
    } catch (e) {
      console.error('Query error:', e);
    } finally {
      setIsQuerying(false); // <-- Apagar al terminar (aunque falle)
    }
  }, [factQuery])

  const handleApplyFilters = useCallback((page = 1) => {
    setCurrentPage(page);
    setShowSuggestions(false);
  }, []);

  // Load config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const conf = await getFactFilters();
        if (conf) {
          setConfig(conf);
          setDynamicYears(conf.years || []);
          const options = conf.availableOptions || {};
          setAvailableOptions(options);

          // 🎯 Pre-selección de segmentos: Todo menos DONACIONES EMPLEADOS y NO APLICA
          const allSegments = options.FACT_SEG_DES || [];
          const excluded = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
          const prefilteredSegments = allSegments.filter(seg => !excluded.includes(seg));

          setFilters(prev => ({
            ...prev,
            FACT_SEG_DES: prefilteredSegments,
            FACT_STATUS_ANULADA: [0 || '0']
          }));
          
          // ✅ Sincronización: Si el año actual no está en la data, usar el último año disponible
          if (conf.years && conf.years.length > 0 && !conf.years.includes(today.getFullYear())) {
            setSelectedYear([conf.years[0]]);
          }
          setSelectedDay([today.getDate()]); // ✅ Preseleccionar el día actual
        }
        
        filtersInitialized.current = true;
      } catch (e) {
        console.error('Config load error:', e);
      }
    };
    loadConfig();
  }, [getFactFilters, today]);

  // ✅ Único punto de ejecución: Reacciona a cambios de filtros o página de forma centralizada
  useEffect(() => {
    if (filtersInitialized.current) {
      const timer = setTimeout(async () => {
        const params = buildQueryParams(currentPage);
        await executeQuery(params);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [selectedYear, selectedMonth, selectedDay, filters, currentPage, buildQueryParams, executeQuery]);

  // ✅ Resetear a página 1 cuando cambian los filtros de búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedMonth, selectedDay, filters]);

  const selectSuggestion = (sug) => {
    setFilters(prev => ({ ...prev, [sug.category]: [sug.value] }));
    setSelectedCategory(sug);
    setSearchValue('');
    setShowSuggestions(false);
    setCurrentPage(1);
  };

  const handleRefresh = useCallback(async () => {
  try {
    setIsQuerying(true);

    // Recargar config SIN destruir UI
    const conf = await getFactFilters();
    let segmentosValidos = [];

    if (conf) {
      setConfig(conf);
      setDynamicYears(conf.years || []);
      const options = conf.availableOptions || {};
      setAvailableOptions(options);

      segmentosValidos = (options.FACT_SEG_DES || []).filter(
        seg => seg !== 'DONACIONES EMPLEADOS' && seg !== 'NO APLICA'
      );
    }

    // Reset filtros
    const resetFilters = {
      ...INITIAL_FILTERS,
      FACT_SEG_DES: segmentosValidos,
      FACT_STATUS_ANULADA: [0]
    };

    setFilters(resetFilters);
    setSearchValue('');
    setSelectedCategory(null);

    setSelectedYear([today.getFullYear()]);
    setSelectedMonth([nombreMesActual]);
    setSelectedDay([today.getDate()]); // ✅ Preseleccionar el día actual

    setCurrentPage(1);

    // Ejecutar query inmediatamente
    const params = {
      page_anios: [today.getFullYear()],
      page_meses: [nombreMesActual],
      page_dias: [today.getDate()], // ✅ Incluir el día actual en la query
      filters: resetFilters,
      page: 1,
      page_size: PAGE_SIZE
    };

    await executeQuery(params);

  } catch (e) {
    console.error('Refresh error:', e);
  } finally {
    setIsQuerying(false);
  }
}, [
  getFactFilters,
  executeQuery,
  today,
  nombreMesActual
]);

  const handleExportExcel = useCallback(async () => {
    if (!config) return;
    setIsQuerying(true);
    try {
      const params = {
        ...buildQueryParams(1),
        page_size: 50000 // Large enough for export
      };
      const result = await factQuery(params);

      const tableData =
        result?.data?.rows ||
        result?.data ||
        result?.table_fact?.rows ||
        result?.table_fact ||
        [];

      if (Array.isArray(tableData) && tableData.length > 0) {
        const data = tableData.map(row => ({
          Fecha: row.display_date || row.FACT_FECHA,
          Factura: row.FACT_FACTURA,
          Cliente: row.FACT_CLIENTE_NOMBRE,
          Producto: row.FACT_PRODUCTO,
          Región: row.FACT_REGION,
          Vendedor: row.FACT_VENDEDOR_ACTUAL,
          Marca: row.FACT_MARCA,
          Línea: row.FACT_LINEA,
          Proveedor: row.FACT_PROVEEDOR,
          Cantidad: Number(row.FACT_CANTIDAD || 0),
          'Descuento_%': Number(row.FACT_DESC || 0),
          'Monto_USD': Number(row.FACT_MONTO_USD || 0),
          Estado: row.FACT_STATUS_ANULADA === 1 ? 'ANULADA' : 'EFECTIVA'
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Facturacion");
        XLSX.writeFile(wb, `Detalle_Facturacion_${new Date().toISOString().split('T')[0]}.xlsx`);
      }
    } catch (e) {
      console.error('Export error:', e);
    } finally {
      setIsQuerying(false);
    }
  }, [config, buildQueryParams, factQuery]);

  const totalPages = useMemo(() => {
    if (globalTotals.totalPages) return Number(globalTotals.totalPages);
    return Math.ceil(totalCount / PAGE_SIZE) || 1;
  }, [totalCount, globalTotals.totalPages]);

  const tableHeaders = [
      'Fecha',
      'Factura',
      'Cliente',
      'Cant. UND',
      'Cant. BL',
      'Monto Bs.',
      'Monto USD',
      'Moneda',
      'Desc.',
      'Neto Bs',
      'Neto USD',
      'Status',
      'PDF'
    ];

  if (loading && !config) {
    return <FactLoader message="Cargando datos de facturación..." />;
  }

  if (error) {
    return (
      <div style={{padding: '40px', textAlign: 'center', color: '#ff6b6b'}}>
        <h3>Error de conexión</h3>
        <p>{error}</p>
        <button onClick={handleRefresh} className="refresh-button" style={{marginTop: '20px'}}>
          <RefreshCw size={20} /> Reintentar
        </button>
      </div>
    );
  }

  return (
    <motion.div className="dashboard-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <FacturacionMenuHeader />
      
      <div style={{ marginTop: '20px' }}>
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Detalle Notas de Crédito</h1>
            <div className="title-underline"></div>
          </div>

          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} title="Reiniciar filtros">
              <RefreshCw size={16} className={loading ? "spin-anim" : ""}/>
            </button>
            <button className="refresh-button" onClick={handleExportExcel} title="Exportar a Excel" style={{ background: '#00ba52' }}>
              <Download size={16} />
            </button>
            
            {/* Nuevo contenedor para agrupar filtros de fecha y búsqueda general */}
            <div className="filters-and-search-group">
              <div className="date-filters-group">
                <MultiSelectFilter label="Día" name="day" icon={Calendar} options={days} selectedValues={selectedDay} onChange={(_, v) => setSelectedDay(v)} />
                <MultiSelectFilter label="Mes" name="month" icon={Calendar} options={meses} selectedValues={selectedMonth} onChange={(_, v) => setSelectedMonth(v)} />
                <MultiSelectFilter label="Año" name="year" icon={Calendar} options={isMobile ? [today.getFullYear()] : dynamicYears} selectedValues={selectedYear} onChange={(_, v) => setSelectedYear(v)} />
              </div>

              <div className="global-search-container" ref={searchRef}>
                <div className="global-search-input-wrapper">
                  <input 
                    type="text" 
                    placeholder="Buscar producto o cliente..." 
                    value={searchValue} 
                    onChange={(e) => setSearchValue(e.target.value)} 
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters(1)} 
                    className="global-search-input" 
                  />
                  <button className="global-search-apply-btn" onClick={() => handleApplyFilters(1)}>
                    <Search size={18} />
                  </button>
                </div>

                <AnimatePresence>
                  {showSuggestions && suggestions.length > 0 && (
                    <motion.div 
                   className="global-search-dropdown"
                      initial={{ opacity: 0, y: -5 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      exit={{ opacity: 0, y: -5 }}
                    >
                      <div className="suggestions-list">
                        {suggestions.map((opt, index) => (
                          <div 
                            key={`${opt.key}-${index}`}
                            className={`suggestion-item ${selectedCategory?.value === opt.value && selectedCategory?.key === opt.key ? 'selected' : ''}`}
                            onClick={() => selectSuggestion(opt)}
                          >
                            <div className="suggestion-category">{opt.catLabel || opt.label}:</div>
                            <div className="suggestion-value">
                              {highlightMatch(opt.value, searchValue)}
                              {filters[opt.category]?.includes(opt.value) && (
                                <span className="suggestion-active">[OK]</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {suggestions.length >= 20 && (
                        <div className="suggestions-footer">
                          Mostrando top 20 resultados...
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <button className="mobile-filter-btn" onClick={() => setShowFilters(true)} style={!isMobile ? { marginLeft: 'auto' } : {}}>
              <Filter size={16} /> <span>Filtros</span>
            </button>
          </div>
        </header>

        <div className={`filters-sidebar-container ${showFilters ? 'open' : ''}`}>
           <div className="filters-sidebar-header">
             <h3>Filtros Avanzados</h3>
             <button className="close-filters-btn" onClick={() => setShowFilters(false)}><X size={20}/></button>
           </div>
           <div className="filters-grid">
             {Object.keys(filters).map(key => (
               <MultiSelectFilter key={key} label={filterLabels[key]} name={key} icon={Tag} options={availableOptions[key]} selectedValues={filters[key]} onChange={(n, v) => setFilters(prev => ({...prev, [n]: v}))} />
             ))}
             <button className="filter-pill-button reset-filters" onClick={() => setFilters(INITIAL_FILTERS)}><FilterX size={12} /> Limpiar</button>
           </div>
        </div>

        {/* Tarjetas de Totales Globales (Filtrados) */}
        <div className="dashboard-indicators3">
          
          <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper">
                <ListPlus size={22} color="white" />
              </div>
              <span className="indicator-label">Total Facturas (Filtro)</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{formatNum(globalTotals.total_facturas)}</span>
            </div>
          </div>
          </div>


          <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper">
                <Boxes size={22} color="white" />
              </div>
              <span className="indicator-label">Total Unidades (Filtro)</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{formatNum(globalTotals.unidad)}</span>
            </div>
          </div>
          </div>    



          <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper">
                <PackagePlus size={22} color="white" />
              </div>
              <span className="indicator-label">Total Bultos (Filtro)</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{formatNum(globalTotals.bultos)}</span>
            </div>
          </div>
          </div>    


          <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper">
                <Banknote size={22} color="white" />
              </div>
              <span className="indicator-label">Total Bs. (Filtro)</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{formatBs(globalTotals.bs)}</span>
            </div>
          </div>
          </div>    
          

          <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper">
                <DollarSign size={22} color="white" />
              </div>
              <span className="indicator-label">Total USD (Filtro)</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{formatUSD(globalTotals.usd)}</span>
            </div>
          </div>
          </div>    
        </div>

        <div className="detail-table-container">
            <div className="table-header">
              <h3>Detalle Notas De Crédito</h3>
              <div className="table-controls">
                <button className="export-table-btn" onClick={handleExportExcel} disabled={rows.length === 0}>
                  <Download size={18} />
                </button>

                <span>Total registros: {totalCount}</span>

                <div className="pagination-controls">
                  <button
                    onClick={() => handleApplyFilters(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isQuerying}
                  >
                    Anterior
                  </button>
                  <span>
                    Página {currentPage} de {totalPages || 1}
                  </span>
                  <button
                    onClick={() => handleApplyFilters(currentPage + 1)}
                    disabled={currentPage >= (totalPages || 1) || isQuerying}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>

            {rows.length === 0 ? (
              <div style={{ padding: '50px', textAlign: 'center', opacity: 0.5 }}>
                No hay registros que coincidan.
              </div>
            ) : (
              <>
                <div className="responsive-table-wrapper" ref={tableContainerRef} style={{ maxHeight: '62vh', overflow: 'auto' }}>
                  <table className="detail-table">
                    <thead>
                      <tr>
                        {tableHeaders.map((header, index) => (
                          <th
                            key={index}
                            style={{
                              textAlign:
                                header.includes('Monto USD') ||
                                header.includes('Cant BL') ||
                                header.includes('Precio')
                                  ? 'right'
                                  : 'left'
                            }}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {groupedRows.map((group, index) => {
                        const isExpanded = expandedRows[group.factura];

                        return (
                          <React.Fragment key={index}>

                            {/* FILA PRINCIPAL */}
                            <tr
                              onClick={() => toggleFactura(group.factura)}
                              style={{
                                opacity: 1,
                              }}
                            >
                              <td>{group.fecha}</td>

                              <td>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}>
                                  {isExpanded ? (
                                    <ChevronDown size={16} />
                                  ) : (
                                    <ChevronRight size={16} />
                                  )}

                                  <span>{group.factura}</span>
                                </div>
                              </td>

                              <td>
                                {group.cliente}
                                <div style={{
                                  fontSize: 11,
                                  opacity: 0.7
                                }}>
                                  ID: {group.clienteId}
                                </div>
                              </td>

                              <td style={{ textAlign: 'right' }}>
                                {formatNum(group.totalCUND)}
                              </td>

                              <td style={{ textAlign: 'right' }}>
                                {formatNum(group.totalCantidad)}
                              </td>

                              <td style={{
                                textAlign: 'right',
                              }}>
                                {formatBs(group.totalBs)}
                              </td>

                              <td style={{
                                textAlign: 'right',
                              }}>
                                {formatUSD(group.totalUSD)}
                              </td>

                              <td>
                                {group.moneda}
                              </td>

                              <td>
                                {group.descuentoPct.toFixed(2) + '%'}
                              </td>

                              <td style={{
                                textAlign: 'right',
                              }}>
                                {formatBs(group.netoFinalBs)}
                              </td>

                              <td style={{
                                textAlign: 'right',
                              }}>
                                {formatUSD(group.netoFinalUSD)}
                              </td>

                              <td>
                                <span
                                  style={{
                                    padding: '4px 10px',
                                    borderRadius: 8,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    background: group.items.some(item => item.FACT_STATUS_ANULADA === 1)
                                      ? 'rgb(101, 0, 0)'
                                      : 'rgb(0, 64, 30)',
                                    color: group.items.some(item => item.FACT_STATUS_ANULADA === 1)
                                      ? '#ff4d4f'
                                      : '#00d26a',
                                    border: group.items.some(item => item.FACT_STATUS_ANULADA === 1)
                                      ? '1px solid rgba(255,77,79,0.35)'
                                      : '1px solid rgba(0,210,106,0.35)'
                                  }}
                                >
                                  {group.items.some(item => item.FACT_STATUS_ANULADA === 1)
                                    ? 'ANULADA'
                                    : 'VIGENTE'}
                                </span>
                              </td>

                            </tr>

                            {/* DETALLES */}
                              {isExpanded && group.items.map((item, subIndex) => {

                                const itemDesc = Number(item.FACT_DESC || 0);

                                const brutoBs = Number(item.FACT_NETO || 0);
                                const brutoUSD = Number(item.FACT_MONTO_USD || 0);

                                const descuentoBs = brutoBs * (itemDesc / 100);
                                const descuentoUSD = brutoUSD * (itemDesc / 100);

                                const netoBs = brutoBs - descuentoBs;
                                const netoUSD = brutoUSD - descuentoUSD;

                                return (
                                  <tr
                                    key={`${index}-${subIndex}`}
                                    style={{
                                      background: 'rgba(255,255,255,0.02)'
                                    }}
                                  >
                                    <td></td>

                                    <td>
                                      {item.FACT_ART_ID}
                                    </td>

                                    <td>
                                      {item.FACT_PRODUCTO}
                                    </td>

                                    <td style={{ textAlign: 'right' }}>
                                      {formatNum(item.FACT_UNIDAD)}
                                    </td>

                                    <td style={{ textAlign: 'right' }}>
                                      {formatNum(item.FACT_CANTIDAD)}
                                    </td>

                                    {/* BRUTO BS */}
                                    <td style={{ textAlign: 'right' }}>
                                      {formatBs(brutoBs)}
                                    </td>

                                    {/* BRUTO USD */}
                                    <td style={{ textAlign: 'right' }}>
                                      {formatUSD(brutoUSD)}
                                    </td>

                                    <td>
                                      {item.FACT_UND}
                                    </td>

                                    {/* DESCUENTO */}
                                    <td style={{ textAlign: 'right' }}>
                                      {itemDesc.toFixed(2)}%
                                    </td>

                                    {/* NETO BS */}
                                    <td style={{ textAlign: 'right' }}>
                                      {formatBs(netoBs)}
                                    </td>

                                    {/* NETO USD */}
                                    <td style={{ textAlign: 'right' }}>
                                      {formatUSD(netoUSD)}
                                    </td>

                                    {/* STATUS */}
                                    <td>
                                      <span
                                        style={{
                                          padding: '3px 8px',
                                          borderRadius: 6,
                                          fontSize: 11,
                                          fontWeight: 600,
                                          background: item.FACT_STATUS_ANULADA === 1
                                            ? 'rgba(255,0,0,0.18)'
                                            : 'rgba(0,255,120,0.18)',
                                          color: item.FACT_STATUS_ANULADA === 1
                                            ? '#ff4d4f'
                                            : '#00d26a',
                                          border: item.FACT_STATUS_ANULADA === 1
                                            ? '1px solid rgba(255,77,79,0.35)'
                                            : '1px solid rgba(0,210,106,0.35)'
                                        }}
                                      >
                                        {item.FACT_STATUS_ANULADA === 1
                                          ? 'ANULADA'
                                          : 'VIGENTE'}
                                      </span>
                                    </td>

                                  </tr>
                                );
                              })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="table-footer-pagination" style={{ display: 'flex', bottom: 0, justifyContent: 'space-between', alignItems: 'center', padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '0 0 12px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-third)' }}>
                      <List size={16} />
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-third)'}}><strong>{formatNum(totalCount)}</strong> registros</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '20px' }}>
                      <DollarSign size={16} color="#00ff88" />
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-third)' }}>Total Pág: <strong>{formatUSD(pageTotalUSD)}</strong></span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.6, color: 'var(--text-third)' }}>
                    <Database size={14} />
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-third)' }}>DimassiDB Core</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
    </motion.div>
  );
};

export default NCredito;